import React, { useEffect, useState } from 'react';
import { Container, Typography, Box, Button, List, ListItem, ListItemText, CircularProgress, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

const STORE_APP_API_URL = process.env.REACT_APP_STORE_APP_API_URL;

const StoreAppsPage = () => {
  const { t } = useTranslation();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Gestion du numéro d'abonnement (subscription number)
  const [subscriptionNumber, setSubscriptionNumber] = useState(() => {
    return localStorage.getItem('subscription_number') || '';
  });
  const [subDialogOpen, setSubDialogOpen] = useState(false);
  const [subInput, setSubInput] = useState(subscriptionNumber);

  // Fonction pour sauvegarder le numéro de subscription (localStorage + backend)
  const saveSubscriptionNumber = async (num) => {
    localStorage.setItem('subscription_number', num);
    setSubscriptionNumber(num);
    setSubDialogOpen(false);
    // Enregistre aussi côté backend
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      await fetch(`${apiUrl}/subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY,
          'x-api-secret': process.env.REACT_APP_API_SECRET
        },
        body: JSON.stringify({ subscription_number: num })
      });
    } catch (e) {
      // Optionnel : afficher une alerte en cas d'erreur
      //console.error('Erreur lors de la sauvegarde du numéro côté backend', e);
    }
  };

  // Au chargement, tente de charger depuis le backend
  useEffect(() => {
    const fetchBackendSub = async () => {
      if (!subscriptionNumber) {
        try {
          const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
          const resp = await fetch(`${apiUrl}/subscription`, {
            headers: {
              'x-api-key': process.env.REACT_APP_API_KEY,
              'x-api-secret': process.env.REACT_APP_API_SECRET
            }
          });
          if (resp.ok) {
            const data = await resp.json();
            if (data.subscription_number) {
              localStorage.setItem('subscription_number', data.subscription_number);
              setSubscriptionNumber(data.subscription_number);
              setSubInput(data.subscription_number);
            }
          }
        } catch (e) {
          // Silencieusement ignorer si non trouvé
        }
      }
    };
    fetchBackendSub();
    // eslint-disable-next-line
  }, []);

  // Si pas de numéro au chargement, demander à l'utilisateur
  useEffect(() => {
    if (!subscriptionNumber) setSubDialogOpen(true);
  }, [subscriptionNumber]);


  useEffect(() => {
    const fetchApps = async () => {
      if (!subscriptionNumber) {
        setError("Numéro d'abonnement introuvable (connectez-vous)");
        return;
      }
      setLoading(true);
      setError('');
      try {
        const apiUrl = STORE_APP_API_URL;
        const response = await fetch(`${apiUrl}/store/store?subscription_number=${subscriptionNumber}`);
        if (!response.ok) throw new Error(await response.text());
        const data = await response.json();
        setApps(data);
      } catch (err) {
        setError(err.message);
        setApps([]);
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
    // eslint-disable-next-line
  }, [subscriptionNumber]);

  const [installedApps, setInstalledApps] = useState([]);

  useEffect(() => {
    const fetchInstalledApps = async () => {
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const appsResp = await fetch(`${backendUrl}/applications/`);
      if (appsResp.ok) {
        const apps = await appsResp.json();
        setInstalledApps(apps.map(app => app.name));
      }
    };
    fetchInstalledApps();
    // eslint-disable-next-line
  }, []);

  // Fonction pour télécharger, dézipper et importer une app personnalisée
  const handleDownload = async (app) => {
    console.log('[StoreApps] Début téléchargement pour', app);

    if (!app.id) {
      alert("ID de l'application manquant pour le téléchargement.");
      return;
    }
    try {
      const apiUrl = STORE_APP_API_URL;
      //console.log('[Téléchargement] Démarrage du téléchargement du zip...');
      console.log('[StoreApps] Téléchargement du zip depuis', `${apiUrl}/store/download-zip/${app.id}`);
      const response = await fetch(`${apiUrl}/store/download-zip/${app.id}`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text();
        alert('[Erreur] Téléchargement du zip: ' + (errorText || 'Erreur lors du téléchargement.'));
        return;
      }
      // Vérification du content-type et de la taille
      const contentType = response.headers.get('content-type');
      if (!contentType || (!contentType.includes('zip') && !contentType.includes('octet-stream'))) {
        alert(`[Erreur] Le fichier téléchargé n'est pas un zip (content-type: ${contentType})`);
        return;
      }
      console.log('[StoreApps] Zip téléchargé, conversion en blob...');
      const zipBlob = await response.blob();
      if (!zipBlob || zipBlob.size === 0) {
        alert('[Erreur] Le zip téléchargé est vide.');
        return;
      }
      //console.log('[Décompression] Décompression du zip...');
      console.log('[StoreApps] Import dynamique JSZip...');
      const JSZip = (await import('jszip')).default;
      //console.log('[Debug] JSZip version:', JSZip.version || 'unknown');
      let zip;
      try {
        console.log('[StoreApps] Décompression du zip...');
        zip = await JSZip.loadAsync(zipBlob);
      } catch (e) {
        alert('[Erreur] Décompression du zip: ' + e.message + '\n' + (e.stack || ''));
        console.error('[Erreur JSZip]', e);
        return;
      }
      // Chercher les fichiers nommés <appname>_config.json, <appname>_code.js, <appname>_svg.svg
      const appNameLower = (app.name || '').toLowerCase();
      const expectedConfig = `${appNameLower}_config.json`;
      const expectedCode = `${appNameLower}_code.js`;
      const expectedSvg1 = `${appNameLower}_svg.svg`;
      const expectedSvg2 = `${appNameLower}_icon.svg`;
      let configFile = null, codeFile = null, iconFile = null;
      console.log('[StoreApps] Recherche des fichiers attendus dans le zip...');
      zip.forEach((relativePath, zipEntry) => {
        const rel = relativePath.toLowerCase();
        if (rel === expectedConfig) configFile = zipEntry;
        else if (rel === expectedCode) codeFile = zipEntry;
        else if (rel === expectedSvg1 || rel === expectedSvg2) iconFile = zipEntry;
      });
      if (!configFile || !codeFile || !iconFile) {
        alert(`[Erreur] Le zip doit contenir les fichiers nommés exactement :\n- ${expectedConfig}\n- ${expectedCode}\n- ${expectedSvg1} ou ${expectedSvg2}`);
        return;
      }
      //console.log('[Lecture] Lecture des fichiers du zip...');
      console.log('[StoreApps] Lecture des fichiers du zip...');
      let configText, codeText, iconText;
      try {
        console.log('[StoreApps] Lecture des fichiers du zip...');
        configText = await configFile.async('text');
        codeText = await codeFile.async('text');
        iconText = await iconFile.async('text');
      } catch (e) {
        alert('[Erreur] Lecture des fichiers du zip: ' + e.message);
        return;
      }
      let appData;
      try {
        console.log('[StoreApps] Parsing JSON de la config...');
        appData = JSON.parse(configText);
      } catch (e) {
        alert('[Erreur] Le fichier de configuration JSON est invalide.');
        return;
      }
      // Retirer l'id si présent
      if ('id' in appData) delete appData.id;
      // Debug : afficher le contenu du code JS extrait
      //console.log('[Debug] codeText:', codeText);
      if (!codeText.trim()) {
        alert('[Erreur] Le fichier code JS extrait du zip est vide.');
        return;
      }
      // Vérifier un seul export default
      const exportCount = (codeText.match(/export default/g) || []).length;
      if (exportCount !== 1) {
        alert(`[Erreur] Le code doit contenir exactement un seul export default (trouvé: ${exportCount}).`);
        return;
      }
      // Upload config
      console.log('[StoreApps] POST /applications/ avec payload:', appData);
      console.log('[StoreApps] Upload de la configuration...');
      const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const checkResp = await fetch(`${backendUrl}/applications/check?name=${encodeURIComponent(appData.name)}`);
      if (checkResp.ok) {
        const exists = await checkResp.json();
        if (exists) {
          alert('Cette application est déjà installée');
          return;
        }
      }
      const configResp = await fetch(`${backendUrl}/applications/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY,
          'x-api-secret': process.env.REACT_APP_API_SECRET
        },
        body: JSON.stringify(appData)
      });
      if (!configResp.ok) {
        let errMsg;
        const contentType = configResp.headers.get('content-type');
        try {
          // Si la réponse est du JSON, on l'utilise
          if (contentType?.includes('application/json')) {
            const jsonError = await configResp.json();
            errMsg = JSON.stringify(jsonError);
          } else {
            // Sinon, on affiche le texte brut (peut être du HTML)
            errMsg = await configResp.text();
            if (errMsg.startsWith('<!doctype')) {
              errMsg = 'Le serveur a retourné une page HTML. Vérifiez l\'URL du backend et les logs serveur.';
            }
          }
        } catch (e) {
          errMsg = 'Erreur inattendue lors de la lecture de la réponse du serveur';
        }
        console.error('[Erreur] Réponse du serveur:', { status: configResp.status, statusText: configResp.statusText, url: configResp.url });
        alert('[Erreur] lors de l\'import de la configuration: ' + errMsg);
        return;
      }
      // Upload code
      //console.log('[Upload] Envoi du code JS...');
      // const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      // Renommage : <AppName>App.js
      const appNameClean = (app.name || '').replace(/\W/g, '');
      const fileName = `${appNameClean}App.js`; // ex: PaintApp.js
      console.log('[StoreApps] Upload du code JS...');
      const codeResp = await fetch(`${backendUrl}/applications/upload-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_API_KEY,
          'x-api-secret': process.env.REACT_APP_API_SECRET
        },
        body: JSON.stringify({ fileName, content: codeText })
      });
      if (!codeResp.ok) {
        const errMsg = await codeResp.text();
        alert('[Erreur] lors de l\'import du code: ' + errMsg);
        return;
      }
      // Upload icône
      //console.log('[Upload] Envoi de l\'icône SVG...');
      const iconFormData = new FormData();
      iconFormData.append('label', appData.name || 'Icône');
      iconFormData.append('value', appNameClean); // ex: Paint
      iconFormData.append('file', new Blob([iconText], { type: 'image/svg+xml' }), `${appNameClean}.svg`); // ex: Paint.svg
      console.log('[StoreApps] Upload de l\'icône SVG...');
      const iconResp = await fetch(`${backendUrl}/icons/icon`, {
        method: 'PUT',
        headers: {
          'x-api-key': process.env.REACT_APP_API_KEY,
          'x-api-secret': process.env.REACT_APP_API_SECRET
        },
        body: iconFormData
      });
      if (!iconResp.ok) {
        const errMsg = await iconResp.text();
        alert('[Erreur] lors de l\'import de l\'icône: ' + errMsg);
        return;
      }
      // Rafraîchir la liste des applications
      //console.log('[Succès] Application importée, rafraîchissement de la liste...');
      if (typeof window.fetch === 'function') {
        console.log('[StoreApps] Rafraîchissement de la liste des applications...');
          fetch(`${backendUrl}/applications/`).then(res => res.json()).then(setApps);
      }
      alert('Application importée avec succès !');
      console.log('[StoreApps] Reload de la page pour afficher la nouvelle application...');
      window.location.reload(); // Rafraîchit toute la page pour afficher la nouvelle application comme après un redémarrage
    } catch (err) {
      alert('[Erreur générale] lors de l\'import: ' + err.message);
      console.error(err);
    }
  };




  return (
    <>
      {/* Dialog pour entrer/modifier le numéro d'abonnement */}
      <Dialog open={subDialogOpen} onClose={() => setSubDialogOpen(false)}>
        <DialogTitle>{t('store.subscription.number')}</DialogTitle>
        <DialogContent>
          <Typography>{t('store.subscription.prompt')}</Typography>
          <TextField
            autoFocus
            margin="dense"
            label={t('store.subscription.number')}
            type="text"
            fullWidth
            value={subInput}
            onChange={e => setSubInput(e.target.value)}
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubDialogOpen(false)} color="secondary">{t('close')}</Button>
          <Button onClick={() => { if(subInput) saveSubscriptionNumber(subInput); }} color="primary" disabled={!subInput}>{t('save', 'Enregistrer')}</Button>
        </DialogActions>
      </Dialog>
      {/* Bouton pour changer le numéro d'abonnement */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button variant="outlined" size="small" onClick={() => setSubDialogOpen(true)}>
          {t('store.subscription.edit')}
        </Button>
      </Box>
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
        <Typography variant="h4" align="center" gutterBottom>
          {t('store.title')}
        </Typography>
        {loading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && !error && (
          <List>
            {apps.length === 0 ? (
              <Typography>Aucune application disponible pour votre abonnement.</Typography>
            ) : (
              apps.map(app => (
                <ListItem key={app.id} divider secondaryAction={
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={installedApps.includes(app.name)}
                    onClick={() => handleDownload(app)}
                  >
                    {installedApps.includes(app.name) ? 'Déjà installé' : 'Installer'}
                  </Button>
                }>
                  {/* Affichage de l'icône */}
                  {app.icon_data ? (
                    <Box sx={{ minWidth: 48, mr: 2, display: 'flex', alignItems: 'center' }}>
                      <img src={app.icon_data} alt={app.name} style={{ width: 32, height: 32, objectFit: 'contain' }} />
                    </Box>
                  ) : (
                    <Box sx={{ minWidth: 48, mr: 2, display: 'flex', alignItems: 'center' }}>
                      <span role="img" aria-label="app-icon">
                        {/* Option : afficher une icône MUI selon app.icon ou une emoji par défaut */}
                        🧩
                      </span>
                    </Box>
                  )}
                  <ListItemText
                    primary={app.name}
                    secondary={app.description || app.publisher || ''}
                  />
                </ListItem>
              ))
            )}
          </List>
        )}
      </Box>
    </Container>
    </>
  );
};

export default StoreAppsPage;
